import React, { useState } from 'react'
import {
  Grid,
  Typography,
  Box,
  IconButton,
  Menu,
  MenuItem,
  SideDrawerWrapper,
} from '@the-platform-group/component-library'
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  ArrowDropDown as ArrowDropDownIcon,
} from '@the-platform-group/component-library/Icons'
import {
  DataGrid,
  GRID_DETAIL_PANEL_TOGGLE_COL_DEF,
} from '@the-platform-group/component-library/DataGrid'
import { DATETIME } from '@the-platform-group/component-library/DataGrid/constant'
import { Button } from '@the-platform-group/component-library/Button'
import { titleCase } from 'change-object-case'

import useTearsheetTemplates from './hooks/useTearsheetTemplates'
import CreateTearsheetTemplateModal from './CreateTearsheetTemplateModal'
import DeleteTearsheetTemplateModal from './DeleteTearsheetTemplateModal'

const NODE_ID_LABELS = {
  reportingHasParent: 'Reporting Organization with a group',
  reportingNoParent: 'Reporting Organization without a group',
  nonReportingHasParent: 'Non-reporting Organization with a group',
  nonReportingNoParent: 'Non-reporting Organization without a group',
}

const TEMPLATE_TYPES_WITH_NODE_IDS = ['organization', 'person']

const TEMPLATE_OPTIONS = [
  { key: 'organization', label: 'Organization' },
  { key: 'person', label: 'Person' },
  { key: 'itinerary', label: 'Itinerary' },
]

const ActionsButtons = ({ row, onEdit, onDelete }) => {
  return (
    <>
      <IconButton size="small" onClick={() => onEdit(row)}>
        <EditIcon />
      </IconButton>
      <IconButton size="small" onClick={() => onDelete(row)}>
        <DeleteIcon />
      </IconButton>
    </>
  )
}

const NodeIdsDetailPanel = ({ row }) => {
  const pages = row.pages || {}
  const rows = Object.entries(NODE_ID_LABELS).map(([key, label]) => ({
    id: key,
    template: label,
    nodeId: pages[key] || '',
  }))

  return (
    <Box sx={{ px: 4, py: 2 }}>
      <DataGrid
        disableColumnFilter
        disableColumnReorder
        disableColumnPinning
        disableExport
        disableDensitySelector
        disableColumnMenu
        disableColumnSelector
        disableMultipleColumnsSorting
        disableReset
        pagination={false}
        autoHeight
        rows={rows}
        columns={[
          {
            field: 'template',
            headerName: 'Template',
            flex: 1,
          },
          {
            field: 'nodeId',
            headerName: 'Node ID',
            flex: 1,
            valueGetter: ({ value }) =>
              value
                ? {
                    to: {
                      pathname: `https://app.sigmacomputing.com/irwin/workbook/${row.externalId}`,
                      search: `?:nodeId=${value}`,
                    },
                    text: value,
                    linkProps: { target: '_blank' },
                  }
                : null,
            type: 'link',
          },
        ]}
        name="tearsheetTemplateNodeIds"
      />
    </Box>
  )
}

const TearsheetTemplates = ({ customer }) => {
  const {
    tearsheetTemplates,
    isFetchingTearsheetTemplates,
    createTemplate,
    isCreateTemplateLoading,
    updateTemplate,
    isUpdateTemplateLoading,
    deleteTemplate,
    isDeleteTemplateLoading,
  } = useTearsheetTemplates(customer)

  const [menuAnchorEl, setMenuAnchorEl] = useState(null)
  const [modalOpen, setModalOpen] = useState(false)
  const [selectedTemplateType, setSelectedTemplateType] = useState(null)
  const [templateToEdit, setTemplateToEdit] = useState(null)
  const [templateToDelete, setTemplateToDelete] = useState(null)

  const menuOpen = Boolean(menuAnchorEl)

  const handleMenuClick = event => {
    setMenuAnchorEl(event.currentTarget)
  }

  const handleMenuClose = () => {
    setMenuAnchorEl(null)
  }

  const handleOptionClick = option => {
    setSelectedTemplateType(option)
    setModalOpen(true)
    handleMenuClose()
  }

  const handleModalClose = () => {
    setModalOpen(false)
    setSelectedTemplateType(null)
    setTemplateToEdit(null)
  }

  const handleEditClick = row => {
    const typeOption = TEMPLATE_OPTIONS.find(opt => opt.key === row.templateType)
    setSelectedTemplateType(typeOption)
    setTemplateToEdit(row)
    setModalOpen(true)
  }

  const handleSaveTemplate = data => {
    const mutate = templateToEdit ? updateTemplate : createTemplate
    mutate(data, {
      onSuccess: handleModalClose,
    })
  }

  const handleDeleteClick = row => {
    setTemplateToDelete(row)
  }

  const handleDeleteModalClose = () => {
    setTemplateToDelete(null)
  }

  const handleDeleteTemplate = id => {
    deleteTemplate(id, {
      onSuccess: handleDeleteModalClose,
    })
  }

  const getDetailPanelContent = React.useCallback(({ row }) => {
    if (!TEMPLATE_TYPES_WITH_NODE_IDS.includes(row.templateType)) return null
    return <NodeIdsDetailPanel row={row} />
  }, [])

  const getDetailPanelHeight = React.useCallback(() => 'auto', [])

  return (
    <SideDrawerWrapper headerProps={{ sx: { pl: 2.5, pb: 0 } }}>
      <Grid container justifyContent="space-between" alignItems="center">
        <Grid item>
          <Typography variant="h6">Tearsheet Templates</Typography>
        </Grid>
        <Grid item>
          <Button onClick={handleMenuClick} endIcon={<ArrowDropDownIcon />}>
            Create Template
          </Button>
          <Menu anchorEl={menuAnchorEl} open={menuOpen} onClose={handleMenuClose}>
            {TEMPLATE_OPTIONS.map(option => (
              <MenuItem key={option.key} onClick={() => handleOptionClick(option)}>
                {option.label}
              </MenuItem>
            ))}
          </Menu>
        </Grid>
        <Grid item xs={12} sx={{ my: 1 }}>
          <DataGrid
            disableExport
            disableMultipleColumnsSorting
            pagination={false}
            autoHeight={false}
            rows={tearsheetTemplates}
            localeText={{
              noRowsLabel: 'No tearsheet templates have been added yet.',
            }}
            columns={[
              {
                ...GRID_DETAIL_PANEL_TOGGLE_COL_DEF,
                maxWidth: 70,
                renderCell: params =>
                  TEMPLATE_TYPES_WITH_NODE_IDS.includes(params.row.templateType)
                    ? GRID_DETAIL_PANEL_TOGGLE_COL_DEF.renderCell(params)
                    : null,
              },
              {
                field: 'name',
                headerName: 'Name',
                flex: 1,
              },
              {
                field: 'description',
                headerName: 'Description',
                flex: 1.5,
              },
              {
                field: 'templateType',
                headerName: 'Type',
                flex: 0.7,
                valueGetter: ({ value }) => (value ? titleCase(value) : ''),
              },
              {
                field: 'externalId',
                headerName: 'Workbook ID',
                flex: 1,
                valueGetter: ({ value }) =>
                  value
                    ? {
                        to: {
                          pathname: `https://app.sigmacomputing.com/irwin/workbook/${value}`,
                        },
                        text: value,
                        linkProps: { target: '_blank' },
                      }
                    : null,
                type: 'link',
              },
              {
                field: 'createdAt',
                headerName: 'Created At',
                type: DATETIME,
                flex: 1,
              },
              {
                field: 'actions',
                type: 'actions',
                headerName: 'Actions',
                getActions: ({ row }) => [
                  <ActionsButtons
                    row={row}
                    onEdit={handleEditClick}
                    onDelete={handleDeleteClick}
                  />,
                ],
              },
            ]}
            name="tearsheetTemplates"
            loading={isFetchingTearsheetTemplates}
            getDetailPanelContent={getDetailPanelContent}
            getDetailPanelHeight={getDetailPanelHeight}
          />
        </Grid>
      </Grid>
      <CreateTearsheetTemplateModal
        open={modalOpen}
        templateType={selectedTemplateType}
        templateToEdit={templateToEdit}
        showNodeIds={TEMPLATE_TYPES_WITH_NODE_IDS.includes(selectedTemplateType?.key)}
        onClose={handleModalClose}
        onSave={handleSaveTemplate}
        isLoading={isCreateTemplateLoading || isUpdateTemplateLoading}
      />
      <DeleteTearsheetTemplateModal
        open={!!templateToDelete}
        template={templateToDelete}
        customerName={customer?.name}
        onClose={handleDeleteModalClose}
        onDelete={handleDeleteTemplate}
        isLoading={isDeleteTemplateLoading}
      />
    </SideDrawerWrapper>
  )
}

export default TearsheetTemplates
